/*
 * Stream console — workspace-scoped real-time view of metadata + data
 * landing on the platform from every connected DHIS2 instance.
 *
 * Layout:
 *   1. Live throughput tiles (.stat-grid) — instances, programs, facts/24h,
 *      tracker events/24h, last activity, sync errors.
 *   2. Per-instance status table — for each DHIS2 service profile owned by
 *      the workspace, show the last metadata sync timestamp + the last
 *      15-minute envelope acceptance counts pulled from /api/app-stream/status.
 *   3. Tabbed live tail — switch between metadata snapshot contents and
 *      the most recent inbound aggregate facts. Polled every 6 s.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { Link } from "react-router-dom";
import { api, loadSession } from "../lib/api.js";
import { useTableState } from "../lib/useTableState.js";
import TableToolbar from "../components/TableToolbar.js";

const POLL_MS = 6000;

type ServiceProfile = {
  environment: string;
  serviceName: string;
  baseUrl: string;
  apiVersion?: string;
  enabled: boolean;
  authMode: string;
  secretConfigured?: boolean;
  lastSuccessfulConnection?: string;
  workspaceId?: string;
  label?: string;
  notes?: string;
};

type AggregateFact = {
  id: string;
  dataElementId: string;
  periodId: string;
  orgUnitId: string;
  categoryOptionComboId: string;
  attributeOptionComboId: string;
  dataSetId?: string;
  value: number;
  lastUpdated?: string;
  auditSource?: string;
  domainIds?: string[];
};

type AggregateMeta = {
  id?: string;
  version?: number;
  createdAt?: string;
  workspaceId?: string;
  serviceProfileId?: string;
  dataSets?: Array<{ id: string; name: string; periodType: string; dataElementIds?: string[] }>;
  dataElements?: Array<{ id: string; name: string; valueType?: string }>;
  orgUnits?: Array<{ id: string; name: string }>;
};

type StreamStatus = {
  workspaceId: string;
  serviceProfileId: string;
  acceptedLast15m: number;
  duplicatesLast15m: number;
  rejectionsLast15m: number;
  lastEnvelopeAt: string | null;
  lastEnvelopeId: string | null;
  serverTime: string;
};

function relTime(iso?: string | null): string {
  if (!iso) return "never";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "—";
  const delta = Date.now() - t;
  if (delta < 0) return "scheduled";
  const mins = Math.floor(delta / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function StreamConsole() {
  const session = loadSession();
  const wsId = session?.workspaceId ?? "";
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [tab, setTab] = useState<"metadata" | "data">("data");
  const [error, setError] = useState<string | null>(null);
  const [profiles, setProfiles] = useState<ServiceProfile[] | null>(null);
  const [meta, setMeta] = useState<AggregateMeta | null>(null);
  const [facts, setFacts] = useState<AggregateFact[] | null>(null);
  const [jobsCounts, setJobsCounts] = useState<{ ok: number; err: number; running: number }>({ ok: 0, err: 0, running: 0 });
  const [statuses, setStatuses] = useState<Record<string, StreamStatus | null>>({});
  const [activeProfile, setActiveProfile] = useState<string>("__all__");

  const refresh = useCallback(async () => {
    try {
      // Workspace-scoped settings give us the profiles the user owns.
      const settings = await api.request<{ serviceProfiles?: ServiceProfile[] }>("/api/settings");
      const profs = (settings.serviceProfiles ?? []).filter((p) => p.serviceName === "dhis2" || p.workspaceId === wsId);
      setProfiles(profs);

      // Snapshot for the active profile (or first owned).
      const target = activeProfile === "__all__" ? profs[0]?.environment : activeProfile;
      if (target) {
        try {
          const m = await api.request<{ snapshot: AggregateMeta | null }>(
            `/api/aggregate-metadata?serviceProfileId=${encodeURIComponent(target)}`
          );
          setMeta(m.snapshot ?? null);
        } catch {
          setMeta(null);
        }
      } else {
        setMeta(null);
      }

      // Inbound facts (workspace-scoped via the platform-side filter).
      try {
        const f = await api.request<{ facts: AggregateFact[] }>("/api/submitted-aggregate/facts");
        setFacts(f.facts ?? []);
      } catch {
        setFacts([]);
      }

      // Job summary (last hour).
      try {
        const j = await api.request<{ jobs: Array<{ status: string; type: string; requestedAt?: string }> }>("/api/jobs");
        const cutoff = Date.now() - 60 * 60 * 1000;
        const recent = (j.jobs ?? []).filter((x) => {
          const t = Date.parse(x.requestedAt ?? "");
          return Number.isFinite(t) ? t >= cutoff : true;
        });
        setJobsCounts({
          ok: recent.filter((x) => x.status === "completed").length,
          err: recent.filter((x) => x.status === "failed" || x.status === "dead-lettered").length,
          running: recent.filter((x) => x.status === "running" || x.status === "queued").length
        });
      } catch {
        setJobsCounts({ ok: 0, err: 0, running: 0 });
      }

      // Per-profile envelope status (last 15m).
      const next: Record<string, StreamStatus | null> = {};
      await Promise.all(
        profs.map(async (p) => {
          try {
            const s = await api.request<StreamStatus>(
              `/api/app-stream/status?workspaceId=${encodeURIComponent(wsId)}&serviceProfileId=${encodeURIComponent(p.environment)}`
            );
            next[p.environment] = s;
          } catch {
            next[p.environment] = null;
          }
        })
      );
      setStatuses(next);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [wsId, activeProfile]);

  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => void refresh(), POLL_MS);
    return () => clearInterval(id);
  }, [refresh, autoRefresh]);

  const recent24hFactCount = useMemo(() => {
    if (!facts) return null;
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    return facts.filter((f) => {
      const t = Date.parse(f.lastUpdated ?? "");
      return Number.isFinite(t) && t >= cutoff;
    }).length;
  }, [facts]);

  const lastActivityIso = useMemo(() => {
    let last: number | null = null;
    for (const s of Object.values(statuses)) {
      if (s?.lastEnvelopeAt) {
        const t = Date.parse(s.lastEnvelopeAt);
        if (Number.isFinite(t) && (last === null || t > last)) last = t;
      }
    }
    if (facts) {
      for (const f of facts.slice(0, 50)) {
        const t = Date.parse(f.lastUpdated ?? "");
        if (Number.isFinite(t) && (last === null || t > last)) last = t;
      }
    }
    return last ? new Date(last).toISOString() : null;
  }, [statuses, facts]);

  const totalDataSets = useMemo(() => meta?.dataSets?.length ?? 0, [meta]);
  const totalDataElements = useMemo(() => meta?.dataElements?.length ?? 0, [meta]);

  const visibleFacts = useMemo(() => {
    if (!facts) return [];
    const sorted = [...facts].sort((a, b) => {
      const at = Date.parse(a.lastUpdated ?? "") || 0;
      const bt = Date.parse(b.lastUpdated ?? "") || 0;
      return bt - at;
    });
    return sorted.slice(0, 50);
  }, [facts]);

  const visibleDataSets = useMemo(() => (meta?.dataSets ?? []).slice(0, 30), [meta]);
  const visibleDataElements = useMemo(() => (meta?.dataElements ?? []).slice(0, 30), [meta]);

  const tProfiles = useTableState((profiles ?? []) as unknown as Array<Record<string, unknown>>, {
    searchFields: ["environment", "label", "baseUrl", "authMode"],
    initialPageSize: 25
  });
  const tFacts = useTableState(visibleFacts as unknown as Array<Record<string, unknown>>, {
    searchFields: ["dataElementId", "periodId", "orgUnitId", "auditSource"],
    initialPageSize: 25
  });
  const tDataSets = useTableState(visibleDataSets as unknown as Array<Record<string, unknown>>, {
    searchFields: ["id", "name", "periodType"],
    initialPageSize: 25
  });
  const tDataElements = useTableState(visibleDataElements as unknown as Array<Record<string, unknown>>, {
    searchFields: ["id", "name", "valueType"],
    initialPageSize: 25
  });

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Stream console</h1>
          <p>Real-time view of every metadata snapshot + data value landing in your TallyStream workspace.</p>
        </div>
        <div className="actions">
          <label className="row" style={{ gap: 6, fontSize: 12, color: "var(--text-muted)" }}>
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
            Auto-refresh
          </label>
          <button type="button" className="btn btn-sm" onClick={() => void refresh()}>Refresh</button>
        </div>
      </header>

      {error && <div className="alert alert-error">{error}</div>}

      <section className="section">
        <div className="stat-grid">
          <div className="stat">
            <div className="stat-label">Connected DHIS2 instances</div>
            <div className="stat-value">{profiles?.length ?? "…"}</div>
            <div className="stat-hint">
              {profiles
                ? `${profiles.filter((p) => p.enabled && (p.authMode === "none" || p.secretConfigured)).length} ready to sync`
                : ""}
            </div>
          </div>
          <div className="stat">
            <div className="stat-label">Programs in scope</div>
            <div className="stat-value">{totalDataSets > 0 ? `${totalDataSets} data sets` : "—"}</div>
            <div className="stat-hint">{totalDataElements > 0 ? `${totalDataElements} data elements` : "Sync metadata to populate"}</div>
          </div>
          <div className="stat">
            <div className="stat-label">Aggregate facts (24 h)</div>
            <div className="stat-value">{recent24hFactCount === null ? "…" : recent24hFactCount.toLocaleString()}</div>
            <div className="stat-hint">{facts ? `${facts.length} total in window` : ""}</div>
          </div>
          <div className="stat">
            <div className="stat-label">Sync jobs (1 h)</div>
            <div className="stat-value">{jobsCounts.ok}</div>
            <div className="stat-hint">
              {jobsCounts.running > 0 ? `${jobsCounts.running} active · ` : ""}
              <span style={{ color: jobsCounts.err > 0 ? "var(--err)" : undefined }}>{jobsCounts.err} failed</span>
            </div>
          </div>
          <div className="stat">
            <div className="stat-label">Last activity</div>
            <div className="stat-value">{relTime(lastActivityIso)}</div>
            <div className="stat-hint">{lastActivityIso ? new Date(lastActivityIso).toLocaleTimeString() : ""}</div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="section-head"><h2>Per-instance ingestion</h2></div>
        {!profiles && <div className="muted">Loading…</div>}
        {profiles && profiles.length === 0 && (
          <div className="card">
            <div className="card-body">
              <p style={{ marginTop: 0 }}>No DHIS2 instances paired to this workspace yet.</p>
              <p>Pair one from the embedded TallyStream DHIS2 app, or <Link to="/app/instances">add a connection</Link>.</p>
            </div>
          </div>
        )}
        {profiles && profiles.length > 0 && (
          <div className="table-scroll">
            <TableToolbar
              search={tProfiles.search} setSearch={tProfiles.setSearch}
              page={tProfiles.page} setPage={tProfiles.setPage}
              pageSize={tProfiles.pageSize} setPageSize={tProfiles.setPageSize}
              totalPages={tProfiles.totalPages} totalRows={tProfiles.totalRows}
              hideExport
              searchPlaceholder="Search instances…"
            />
            <table className="data-table">
              <thead>
                <tr>
                  <th>Instance</th>
                  <th>Base URL</th>
                  <th>Auth</th>
                  <th>Last sync</th>
                  <th>Accepted (15 m)</th>
                  <th>Duplicates</th>
                  <th>Rejected</th>
                </tr>
              </thead>
              <tbody>
                {(tProfiles.paged as unknown as ServiceProfile[]).map((p) => {
                  const s = statuses[p.environment];
                  return (
                    <tr key={p.environment}>
                      <td>
                        <strong>{p.label || p.environment}</strong>
                        {!p.enabled && <span className="pill pill-muted" style={{ marginLeft: 6 }}>disabled</span>}
                        {p.enabled && p.authMode !== "none" && !p.secretConfigured && (
                          <span className="pill pill-warn" style={{ marginLeft: 6 }}>no credential</span>
                        )}
                      </td>
                      <td><span className="mono" style={{ fontSize: 12 }}>{p.baseUrl}</span></td>
                      <td>{p.authMode}</td>
                      <td>{relTime(p.lastSuccessfulConnection)}</td>
                      <td>{s?.acceptedLast15m ?? 0}</td>
                      <td>{s?.duplicatesLast15m ?? 0}</td>
                      <td style={{ color: (s?.rejectionsLast15m ?? 0) > 0 ? "var(--err)" : undefined }}>
                        {s?.rejectionsLast15m ?? 0}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="section">
        <div className="section-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div className="row" style={{ gap: 8 }}>
            <button
              type="button"
              className={`btn btn-sm ${tab === "data" ? "btn-primary" : ""}`}
              onClick={() => setTab("data")}
            >
              Inbound data values
            </button>
            <button
              type="button"
              className={`btn btn-sm ${tab === "metadata" ? "btn-primary" : ""}`}
              onClick={() => setTab("metadata")}
            >
              Inbound metadata
            </button>
          </div>
          <div className="row" style={{ gap: 8 }}>
            <label className="muted" style={{ fontSize: 12 }}>Filter:</label>
            <select value={activeProfile} onChange={(e) => setActiveProfile(e.target.value)} style={{ width: "auto", minHeight: 28, padding: "4px 8px" }}>
              <option value="__all__">All instances</option>
              {(profiles ?? []).map((p) => (
                <option key={p.environment} value={p.environment}>{p.label || p.environment}</option>
              ))}
            </select>
          </div>
        </div>

        {tab === "data" && (
          <div className="table-scroll">
            <TableToolbar
              search={tFacts.search} setSearch={tFacts.setSearch}
              page={tFacts.page} setPage={tFacts.setPage}
              pageSize={tFacts.pageSize} setPageSize={tFacts.setPageSize}
              totalPages={tFacts.totalPages} totalRows={tFacts.totalRows}
              hideExport
              searchPlaceholder="Search inbound values…"
            />
            <table className="data-table">
              <thead>
                <tr>
                  <th>Received</th>
                  <th>Data element</th>
                  <th>Period</th>
                  <th>Org unit</th>
                  <th>Value</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {!facts && (
                  <tr><td colSpan={6} className="muted">Loading…</td></tr>
                )}
                {facts && visibleFacts.length === 0 && (
                  <tr><td colSpan={6} className="muted">
                    No aggregate data values received yet. Run an ingestion from the embedded app or wait for the next scheduled sync.
                  </td></tr>
                )}
                {(tFacts.paged as unknown as AggregateFact[]).map((f) => (
                  <tr key={f.id}>
                    <td className="muted">{relTime(f.lastUpdated)}</td>
                    <td className="mono" style={{ fontSize: 12 }}>{f.dataElementId}</td>
                    <td>{f.periodId}</td>
                    <td className="mono" style={{ fontSize: 12 }}>{f.orgUnitId}</td>
                    <td>{f.value}</td>
                    <td className="muted" style={{ fontSize: 12 }}>{f.auditSource ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "metadata" && (
          <>
            {!meta && <div className="muted" style={{ padding: 16 }}>Loading metadata snapshot…</div>}
            {meta && (
              <>
                <div className="card" style={{ marginBottom: 12 }}>
                  <div className="card-head">
                    <strong>Snapshot</strong>
                    <span className="muted" style={{ fontSize: 12 }}>
                      v{meta.version} · {meta.createdAt ? new Date(meta.createdAt).toLocaleString() : "—"}
                      {meta.serviceProfileId ? ` · ${meta.serviceProfileId}` : ""}
                    </span>
                  </div>
                </div>
                <div className="row-wrap" style={{ alignItems: "stretch" }}>
                  <div className="card" style={{ flex: 1, minWidth: 320 }}>
                    <div className="card-head"><strong>Data sets</strong><span className="muted">{meta.dataSets?.length ?? 0}</span></div>
                    <div className="table-scroll" style={{ maxHeight: 320 }}>
                      <TableToolbar
                        search={tDataSets.search} setSearch={tDataSets.setSearch}
                        page={tDataSets.page} setPage={tDataSets.setPage}
                        pageSize={tDataSets.pageSize} setPageSize={tDataSets.setPageSize}
                        totalPages={tDataSets.totalPages} totalRows={tDataSets.totalRows}
                        hideExport
                        searchPlaceholder="Search data sets…"
                      />
                      <table className="data-table">
                        <thead><tr><th>ID</th><th>Name</th><th>Period</th></tr></thead>
                        <tbody>
                          {visibleDataSets.length === 0 && (
                            <tr><td colSpan={3} className="muted">No data sets in current snapshot.</td></tr>
                          )}
                          {(tDataSets.paged as unknown as Array<{ id: string; name: string; periodType: string }>).map((d) => (
                            <tr key={d.id}>
                              <td className="mono" style={{ fontSize: 12 }}>{d.id}</td>
                              <td>{d.name}</td>
                              <td>{d.periodType}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="card" style={{ flex: 1, minWidth: 320 }}>
                    <div className="card-head"><strong>Data elements</strong><span className="muted">{meta.dataElements?.length ?? 0}</span></div>
                    <div className="table-scroll" style={{ maxHeight: 320 }}>
                      <TableToolbar
                        search={tDataElements.search} setSearch={tDataElements.setSearch}
                        page={tDataElements.page} setPage={tDataElements.setPage}
                        pageSize={tDataElements.pageSize} setPageSize={tDataElements.setPageSize}
                        totalPages={tDataElements.totalPages} totalRows={tDataElements.totalRows}
                        hideExport
                        searchPlaceholder="Search data elements…"
                      />
                      <table className="data-table">
                        <thead><tr><th>ID</th><th>Name</th><th>Type</th></tr></thead>
                        <tbody>
                          {visibleDataElements.length === 0 && (
                            <tr><td colSpan={3} className="muted">No data elements in current snapshot.</td></tr>
                          )}
                          {(tDataElements.paged as unknown as Array<{ id: string; name: string; valueType?: string }>).map((de) => (
                            <tr key={de.id}>
                              <td className="mono" style={{ fontSize: 12 }}>{de.id}</td>
                              <td>{de.name}</td>
                              <td>{de.valueType ?? "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </section>
    </div>
  );
}
